import { IconBrandGoogle, IconCalendarCheck } from '@tabler/icons-react';
import { Button } from '@petseo/shared/components/layout/common';
import { LoadingSpinner, WarningAlert, InfoAlert, FormCancelButton, withModalMount } from '@petseo/shared';
import { FormModal } from '@/components/FormModal';
import { useEmployeeCalendarStatus, useConnectGoogleCalendar, useDisconnectGoogleCalendar } from '../../hooks';
import type { ServiceVendorId, EmployeeDto } from '@/services/api/petseo.client.wrapper';
import { createEmployeeId } from '@/services/api/petseo.client.wrapper';
import { formatDateTimePolish } from '@/utils/datetime/date';
import { GOOGLE_CALENDAR_CONNECTION_STATUS, GOOGLE_CALENDAR_REVOKE_REASON } from '@/constants';

interface EmployeeCalendarModalProps {
  serviceVendorId: ServiceVendorId;
  employee?: EmployeeDto;
  isOpen: boolean;
  onClose: () => void;
}

const REVOKE_ALERTS: Record<string, { title: string; message: string }> = {
  [GOOGLE_CALENDAR_REVOKE_REASON.INSUFFICIENT_SCOPES]: {
    title: 'Brak zgody na dostęp do kalendarza',
    message: 'Podczas łączenia nie została zaznaczona zgoda na dostęp do Kalendarza Google. Połącz ponownie i zaznacz tę zgodę.',
  },
  [GOOGLE_CALENDAR_REVOKE_REASON.TOKEN_REVOKED]: {
    title: 'Dostęp do kalendarza wygasł',
    message: 'Dostęp wygasł lub został cofnięty w koncie Google. Połącz ponownie, aby wznowić synchronizację rezerwacji.',
  },
  [GOOGLE_CALENDAR_REVOKE_REASON.CALENDAR_NOT_FOUND]: {
    title: 'Kalendarz jest niedostępny',
    message: 'Kalendarz został usunięty w koncie Google lub przestał być udostępniony. Połącz ponownie, wybierając istniejący kalendarz.',
  },
};

const DEFAULT_REVOKE_ALERT = {
  title: 'Połączenie z kalendarzem zostało przerwane',
  message: 'Połącz ponownie, aby wznowić synchronizację rezerwacji.',
};

function EmployeeCalendarModalContent({ serviceVendorId, employee, isOpen, onClose }: EmployeeCalendarModalProps) {
  const employeeId = employee?.id ? createEmployeeId(employee.id) : undefined;

  const { data, isLoading } = useEmployeeCalendarStatus(serviceVendorId, employeeId!);
  const connectCalendar = useConnectGoogleCalendar();
  const disconnectCalendar = useDisconnectGoogleCalendar();

  if (!employee?.id || !employeeId) return null;

  const handleConnect = () => connectCalendar.mutate({ serviceVendorId, employeeId });
  const handleDisconnect = () => disconnectCalendar.mutate({ serviceVendorId, employeeId });

  const isConnected = !!data?.isConnected;
  const needsReconnect = isConnected && data?.status === GOOGLE_CALENDAR_CONNECTION_STATUS.REVOKED;
  const isRetrying = isConnected && data?.status === GOOGLE_CALENDAR_CONNECTION_STATUS.ERROR;
  const revokeAlert = (data?.revokeReason && REVOKE_ALERTS[data.revokeReason]) || DEFAULT_REVOKE_ALERT;
  const lastSynced = formatDateTimePolish(data?.lastSyncedAtUtc);

  const title = (
    <span className="d-flex align-items-center">
      <IconBrandGoogle size={20} className="me-2 text-secondary" />
      Kalendarz Google — {employee.firstName} {employee.lastName}
    </span>
  );

  return (
    <FormModal isOpen={isOpen} onClose={onClose} title={title} size="lg">
      <div className="modal-body">
        {isLoading ? (
          <LoadingSpinner />
        ) : isConnected ? (
          <>
            {needsReconnect && <WarningAlert title={revokeAlert.title} message={revokeAlert.message} className="mb-3" />}

            {isRetrying && (
              <InfoAlert
                title="Synchronizacja chwilowo nie działa"
                message="Ostatnia próba synchronizacji się nie powiodła. Ponawiamy ją automatycznie co kilkanaście minut. Jeśli problem się utrzymuje, połącz kalendarz ponownie."
                className="mb-3"
              />
            )}

            {!needsReconnect && (
              <div className="d-flex align-items-center gap-2 mb-2">
                <IconCalendarCheck size={18} className="text-success" />
                <span className="fw-medium">Połączono z Kalendarzem Google</span>
              </div>
            )}

            {data?.accountEmail && <div className="text-secondary mb-1">{data.accountEmail}</div>}

            {lastSynced && <div className="text-muted small">Ostatnia synchronizacja: {lastSynced}</div>}
          </>
        ) : (
          <p className="text-secondary mb-0">
            Połącz kalendarz Google pracownika, aby automatycznie synchronizować rezerwacje i blokować zajęte terminy.
          </p>
        )}
      </div>

      <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        <FormCancelButton onClick={onClose} />

        <div className="d-flex gap-2">
          {isConnected && !needsReconnect && (
            <Button variant="danger" onClick={handleDisconnect} isLoading={disconnectCalendar.isPending}>
              Rozłącz
            </Button>
          )}

          {(!isConnected || needsReconnect || isRetrying) && (
            <Button variant="primary" onClick={handleConnect} isLoading={connectCalendar.isPending} icon={<IconBrandGoogle size={16} />}>
              {needsReconnect || isRetrying ? 'Połącz ponownie' : 'Podłącz kalendarz'}
            </Button>
          )}
        </div>
      </div>
    </FormModal>
  );
}

export const EmployeeCalendarModal = withModalMount(EmployeeCalendarModalContent);
